import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import webservice, { MobileServicePath } from '../../../../services/webservice';
import AuditLogFilter from './AuditLogFilter';
import AuditLogStats from './AuditLogStats';
import AuditLogList from './AuditLogList';
import './AuditLog.css';

const UserAPIAnalytics = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({});

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Sorting state
    const [sortBy, setSortBy] = useState('timestamp');
    const [sortOrder, setSortOrder] = useState('desc');

    // Stats state
    const [statsData, setStatsData] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);

    // Read tab state passed from navigate
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(location.state?.tab || 'web');

    // navigate
    const navigate = useNavigate();

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchAuditLog(filters, currentPage, itemsPerPage, activeTab, sortBy, sortOrder);
        }, 100);
        return () => clearTimeout(handler);
    }, [filters, currentPage, itemsPerPage, activeTab, sortBy, sortOrder]);

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchStats(filters, activeTab);
        }, 100);
        return () => clearTimeout(handler);
    }, [filters, activeTab]);

    const fetchStats = async (currentFilters, currentTab) => {
        setStatsLoading(true);
        setStatsData(null); // Clear previous stats before fetching
        try {
            const response = await axios.post(`${webservice}api/user-api-auditlog-stats`, {
                params: { ...currentFilters, tab: currentTab }
            });
            if (response.data && response.data.success) {
                setStatsData(response.data.data);
            }
        } catch (err) {
            console.error('Failed to fetch User API Audit Log Stats:', err);
        } finally {
            setStatsLoading(false);
        }
    };

    const fetchAuditLog = async (currentFilters, page, limit, currentTab, currentSortBy, currentSortOrder) => {
        setLoading(true);
        setError('');
        setData([]); // Clear previous logs before fetching
        try {
            const payload = {
                ...currentFilters,
                page: page,
                limit: limit,
                tab: currentTab,
                sortBy: currentSortBy,
                sortOrder: currentSortOrder
            };

            // Using a placeholder endpoint based on the standard backend structure.
            // Ensure this endpoint exists in the backend and handles these filter params.
            const response = await axios.post(`${webservice}api/user-api-auditlog`, {
                params: payload
            });

            // Adjust data parsing based on the actual API response structure
            if (response.data && response.data.data) {
                setData(response.data.data);
               // console.log(response.data.data)
                setTotalRecords(response.data.total || 0);
                const limitVal = response.data.limit || limit;
                const total = response.data.total || 0;
                setTotalPages(Math.ceil(total / limitVal));
            } else if (Array.isArray(response.data)) {
              //  console.log(response.data)
                setData(response.data);
                setTotalRecords(response.data.length);
                setTotalPages(1);
            } else {
                setData([]);
                setTotalRecords(0);
                setTotalPages(0);
            }
        } catch (err) {
            console.error('Failed to fetch User API Audit Log:', err);
            setError('Failed to load audit logs. Please try again later.');
            setData([]);
            setTotalRecords(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
        setCurrentPage(1); // Reset to first page on filter change
    }, []);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1);
    };

    const handleSortChange = (field, order) => {
        setSortBy(field);
        setSortOrder(order);
        setCurrentPage(1);
    };

    return (
        <div className="audit-log-container">
            <div className="audit-log-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button
                        onClick={() => navigate('/api-analytics')}
                        className="btn btn-primary"
                        style={{ padding: '6px 16px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 0, width: 'auto', flex: '0 0 auto' }}
                    >
                        <i className="fa fa-arrow-left"></i> Back
                    </button>
                    <h2>User API Audit Log ({activeTab === 'mobile' ? 'Mobile API' : 'Web API'})</h2>
                </div>
            </div>

            <AuditLogStats data={statsData} loading={statsLoading} />
            <AuditLogFilter onFilterChange={handleFilterChange} />

            {error && <div className="text-danger mb-4">{error}</div>}

            {loading ? (
                <div className="d-flex justify-content-center my-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : (
                <AuditLogList
                    data={data}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    totalRecords={totalRecords}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={handleSortChange}
                />
            )}
        </div>
    );
};

export default UserAPIAnalytics;
